import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useGetAllFundSources, useGetUser, useIssueBalance } from '../../../../libs/admin/hooks'
import ArrowBack from '~icons/material-symbols/arrow-back.svg'
import ArrowForward from '~icons/material-symbols/arrow-forward.svg'
import CheckCircle from '~icons/material-symbols/check-circle.svg'
import { css } from '../../../../../styled-system/css'
import { contentWrapper } from '../../../../styles/contentWrapper'
import { titleWrapper, pageTitle, sectionWrapper } from '../../../../styles/sectionStyles'
import { LoadingOverlay } from '../../../../components/loading-overlay'
import { formatTimestampJaJst } from '../../../../libs/utils/format-timestamp'
import { textLabel, text } from '../../../../styles/textStyles'
import { formatGender } from '../../../../libs/utils/format-gender'
import { ValidatableTextInput } from '../../../../components/validatable-inputs/text-input'
import { ValidatableSelect } from '../../../../components/validatable-inputs/select'
import { useState } from 'react'
import { ValidatableDayPicker } from '../../../../components/validatable-inputs/day-picker'
import { IssueBalanceSchema } from '../-schemas/issue-balance'
import type { z } from 'zod'
import { Button, Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import { button } from '../../../../styles/buttonStyles'
import { dialogBackdrop } from '../../../../styles/dialogStyles'
import { Timestamp } from '@bufbuild/protobuf'
import { endOfDay, format } from 'date-fns'

const ADMIN_GRANT_FUNDSOURCE_TYPE = "AdminGrant"

export const Route = createFileRoute('/admin/users/$subscriptionOrUserId/issueBalance')({
  component: RouteComponent,
})

const useIssueBalanceData = () => {
  const { subscriptionOrUserId } = Route.useParams()
  const { data: allFundSources, isLoading: isFundSourcesLoading } = useGetAllFundSources()
  const { data: user, isLoading: isUserLoading } = useGetUser({ subscriptionOrUserId })
  const isLoading = isFundSourcesLoading || isUserLoading
  const fundSources = allFundSources?.filter(
    // 付与してよいものだけ表示
    fundSource => fundSource.type !== ""
  ).sort(
    (a, b) => {
      return a.name.localeCompare(b.name)
    }
  )
  return {
    isLoading,
    fundSources: fundSources || [],
    user: user?.user,
  }
}

function RouteComponent() {
  return <Layout />
}

const BreadCrumbs = () => {
  const { user, isLoading } = useIssueBalanceData()
  if (isLoading) {
    return null
  }
  if (user === undefined) {
    return null
  }

  return (
    <div className={css({
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    })}>
      <Link
        to="/admin/users"
      >
        <span className={css({
          color: 'text.accentPrimary',
          fontWeight: '400',
        })}>
          利用者検索
        </span>
      </Link>
      <span className={css({
        color: 'text.tertiary',
      })}>/</span>
      <Link
        to=".."
      >
        <span className={css({
          color: 'text.accentPrimary',
          fontWeight: '400',
        })}>
          {user.name}
        </span>
      </Link>
      <span className={css({
        color: 'text.tertiary',
      })}>/</span>
      <span className={css({
        color: 'text.tertiary',
      })}>ポイント発行</span>
    </div>
  )
}

type issueBalanceDataType = z.infer<typeof IssueBalanceSchema>

const IssuePointPane = () => {
  const navigate = useNavigate()
  const { user, fundSources, isLoading } = useIssueBalanceData()
  const { mutateAsync: issueBalance, isPending: isIssueBalancePending } = useIssueBalance()
  const [dialogStep, setDialogStep] = useState(0)
  const [dirtyIssueBalanceData, setDirtyIssueBalanceData] = useState<Partial<issueBalanceDataType>>({
    fundSourceId: "",
    amount: 0,
  })
  if (user === undefined || fundSources === undefined) {
    return null
  }

  const parsedData = IssueBalanceSchema.safeParse(dirtyIssueBalanceData)
  const canSubmit = !isIssueBalancePending && parsedData.success
  const errors = parsedData.error?.formErrors.fieldErrors

  const handleFundSourceChange = (id: string) => {
    const fundSource = fundSources.find(fundSource => fundSource.id === id)
    if (!fundSource) {
      setDirtyIssueBalanceData((prev) => ({ ...prev, fundSourceId: "" }))
      setDirtyIssueBalanceData((prev) => ({ ...prev, reason: "" }))
      return;
    }
    setDirtyIssueBalanceData((prev) => ({ ...prev, fundSourceId: id }))
    setDirtyIssueBalanceData((prev) => ({ ...prev, reason: fundSource?.note || "" }))
  }

  const fundSourceOptions = [
    {
      label: "選択してください",
      value: ""
    },
    ...fundSources.filter(
      fundSource => fundSource.type === ADMIN_GRANT_FUNDSOURCE_TYPE // Only show fund sources that can be used for issuing points
    ).map((fundSource) => ({
      label: fundSource.name,
      value: fundSource.id
    }))
  ]

  const handleShowConfirmation = () => {
    if (canSubmit) {
      setDialogStep(1)
    }
  }

  const handleIssueBalance = async () => {
    if (!parsedData.success) {
      return;
    }
    const amount = BigInt(parsedData.data.amount)
    if (Number(amount) !== parsedData.data.amount) {
      console.error("Failed to issue balance: bad amount value", parsedData.data.amount)
      throw new Error("発行ポイント数が不正です。")
    }

    try {
      await issueBalance({
        userId: user.id, // using userId instead of subscriptionId. Maybe FIXME?
        fundSourceId: parsedData.data.fundSourceId,
        amount,
        expiresAt: Timestamp.fromDate(parsedData.data.validUntil),
        reason: parsedData.data.reason || "",
      })

      setDirtyIssueBalanceData({
        fundSourceId: "",
        amount: 0,
      })

      setDialogStep(2) // Show success dialog
    } catch (error) {
      console.error("Failed to issue balance:", error)
      throw error
    }
  }

  const handleClose = () => {
    navigate({ to: '..' })
  }

  return (
    <div className={css({
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      gap: '16px',
    })}>
      <div className={css(
        sectionWrapper.raw(),
      )}>
        {isLoading && <LoadingOverlay />}
        <div className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        })}>
          <h3 className={css({
            fontSize: '18px',
            fontWeight: '600',
            color: 'text.primary',
          })}>ポイント発行設定</h3>
          <ValidatableSelect
            title="ポイント種類"
            value={dirtyIssueBalanceData.fundSourceId || ""}
            onChange={handleFundSourceChange}
            options={fundSourceOptions}
            isValid={!errors?.fundSourceId}
            isRequired={true}
            disabled={false}
            validationMessage={errors?.fundSourceId?.[0] ?? "OK"}
          />
          <ValidatableTextInput
            title="発行ポイント数"
            value={dirtyIssueBalanceData.amount?.toString() || "0"}
            onChange={(value) => setDirtyIssueBalanceData((prev) => ({ ...prev, amount: Number.parseInt(value) || 0 }))}
            type="number"
            isValid={!errors?.amount}
            isRequired={true}
            disabled={false}
            validationMessage={errors?.amount?.[0] ?? "OK"}
            placeholder="0"
          />
          <ValidatableDayPicker
            title='有効期限'
            description='指定した日付の23:59:59まで有効です'
            value={dirtyIssueBalanceData.validUntil}
            onChange={(date) => setDirtyIssueBalanceData((prev) => ({
              ...prev,
              validUntil: date ? endOfDay(date) : undefined
            }))}
            isValid={!errors?.validUntil}
            isRequired={true}
            disabled={false}
            validationMessage={errors?.validUntil?.[0] ?? "OK"}
            placeholder="有効期限"
          />
          <ValidatableTextInput
            title="発行理由"
            description='記載内容はユーザが閲覧できます'
            value={dirtyIssueBalanceData.reason || ""}
            onChange={(value) => setDirtyIssueBalanceData((prev) => ({ ...prev, reason: value }))}
            type="text"
            isValid={!errors?.reason}
            disabled={false}
            validationMessage={errors?.reason?.[0] ?? "OK"}
            placeholder="発行理由"
            multiLine={true}
          />
        </div>
      </div>
      <Button
        className={button({ color: 'primary' })}
        disabled={!parsedData.success || isLoading}
        onClick={handleShowConfirmation}
      >
        ポイント発行
        <ArrowForward />
      </Button>

      {/* Confirmation Dialog */}
      <Dialog
        open={dialogStep === 1}
        onClose={() => setDialogStep(0)}
        className={dialogBackdrop()}
      >
        <DialogPanel className={css({
          backgroundColor: 'white',
          width: '400px',
          margin: 'auto',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        })}>
          <DialogTitle
            className={css({
              fontSize: '18px',
              fontWeight: 'bold',
              textAlign: 'center',
            })}
          >
            ポイントを発行しますか?
          </DialogTitle>
          <div className={css({
            fontSize: '14px',
            textAlign: 'center',
          })}>
            内容をご確認のうえ、ポイントを発行してください。
          </div>
          <div className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            padding: '16px',
            backgroundColor: 'surface.secondary',
            borderRadius: '8px',
          })}>
            <div>発行対象者：{user.name}</div>
            <div>ポイント種類：{fundSources.find(fs => fs.id === dirtyIssueBalanceData.fundSourceId)?.name}</div>
            <div>ポイント数：{dirtyIssueBalanceData.amount}</div>
            <div>有効期限：{dirtyIssueBalanceData.validUntil ? format(dirtyIssueBalanceData.validUntil, "yyyy年M月d日") : ""}</div>
          </div>
          <div className={css({
            display: 'flex',
            justifyContent: 'center',
            gap: '8px',
          })}>
            <button
              type='button'
              className={button({ color: 'ghost' })}
              onClick={() => setDialogStep(0)}
            >
              もどる
            </button>
            <button
              type='button'
              className={button({ color: 'primary' })}
              onClick={handleIssueBalance}
              disabled={isIssueBalancePending}
            >
              ポイント発行
            </button>
          </div>
        </DialogPanel>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={dialogStep === 2}
        onClose={handleClose}
        className={dialogBackdrop()}
      >
        <DialogPanel className={css({
          backgroundColor: 'white',
          width: '400px',
          margin: 'auto',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          alignItems: 'center',
        })}>
          <CheckCircle className={css({
            width: '96px',
            height: '96px',
            color: 'text.accentSuccess',
            margin: '0 auto',
          })} />
          <DialogTitle
            className={css({
              fontSize: '18px',
              fontWeight: 'bold',
              textAlign: 'center',
            })}
          >
            ポイントを発行しました
          </DialogTitle>
          <div className={css({
            display: 'flex',
            justifyContent: 'center',
          })}>
            <button
              type='button'
              className={button({ color: 'ghost' })}
              onClick={handleClose}
            >
              閉じる
            </button>
          </div>
        </DialogPanel>
      </Dialog>
    </div>
  )
}

const UserInfoPane = () => {
  const { user, isLoading } = useIssueBalanceData()
  if (user === undefined) {
    return null
  }

  return (
    <div className={css(
      sectionWrapper.raw(),
      {
        backgroundColor: "transparent",
      }
    )}>
      {isLoading && <LoadingOverlay />}
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      })}>
        <h3 className={css({
          fontSize: '18px',
          fontWeight: '600',
          color: 'text.primary',
        })}>発行対象者情報</h3>
        <div>
          <div className={textLabel()}>氏名</div>
          <div className={text()}>{user.name || "\u00a0"}</div>
        </div>
        <div>
          <div className={textLabel()}>性別</div>
          <div className={text()}>{formatGender(user.gender) || "\u00a0"}</div>
        </div>
        <div>
          <div className={textLabel()}>市区町村</div>
          <div className={text()}>{user.cityAddress || "\u00a0"}</div>
        </div>
        <div>
          <div className={textLabel()}>生年月日</div>
          <div className={text()}>{user.birthday ? formatTimestampJaJst(user.birthday) : "\u00a0"}</div>
        </div>
        <div>
          <div className={textLabel()}>サブスクリプションID</div>
          <div className={text()}>{user.subscriptionId || "\u00a0"}</div>
        </div>
        <div>
          <div className={textLabel()}>登録日時</div>
          <div className={text()}>{user.createdAt ? formatTimestampJaJst(user.createdAt) : "\u00a0"}</div>
        </div>
      </div>
    </div>
  )
}

const Layout = () => {
  return (
    <div className={contentWrapper()}>
      <div className={titleWrapper()}>
        <h1 className={pageTitle()}>
          <div className={css({ display: 'flex', alignItems: 'center', gap: '8px' })}>
            <Link to='..'>
              <ArrowBack />
            </Link>
            利用者詳細
          </div>
        </h1>
      </div>
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      })}>
        <BreadCrumbs />
        <div className={css({
          display: 'flex',
          flexDirection: 'row',
          gap: '16px',
        })}>
          <IssuePointPane />
          <UserInfoPane />
        </div>
      </div>
    </div>
  )
}
